import { useId, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CloseIcon,
  Dialog,
  IconButton,
  Stack,
  Text,
  TextField,
  WarningIcon,
} from '@citibox-panel/ui';
import { useNoticeOnError } from '../../search-shell/hooks/useErrorNotice';
import { normalizeReason } from '../lib/reason';
import { ReasonField } from './ReasonField';

// TODO: i18n
const WRITE_ERROR = 'No se ha podido eliminar la cuenta. Vuelve a intentarlo.';
const NOTICE_ID = 'delete-account';
// TODO: i18n — copy en ES hasta Fase 1.
const COPY = {
  user: { heading: '¿Eliminar cuenta de usuario?', label: 'Eliminar cuenta de usuario' },
  courier: { heading: '¿Eliminar cuenta de mensajero?', label: 'Eliminar cuenta de mensajero' },
};

export interface DeleteAccountDialogProps {
  open: boolean;
  onCancel: () => void;
  // The reason only on a courier account: a «Usuario» baja hands over the exclusion alone.
  onConfirm: (amazonExclusion: boolean, reason?: string) => void;
  isPending: boolean;
  isError: boolean;
  // Whether to offer the Amazon carrier-exclusion section at all. Driven by the account's Amazon
  // delivery history: an account that never received an Amazon parcel cannot be excluded from a
  // list it was never on, so the section is hidden outright rather than shown inert. When hidden,
  // the exclusion stays false — nothing to opt into.
  showAmazonExclusion: boolean;
  // A courier account's baja also asks for the «Motivo»; every other account's stays as it was.
  courier: boolean;
}

export function DeleteAccountDialog({
  open,
  onCancel,
  onConfirm,
  isPending,
  isError,
  showAmazonExclusion,
  courier,
}: DeleteAccountDialogProps) {
  // Starts UNCHECKED and is never seeded from anything: the exclusion is a second, separate
  // decision that happens to be taken on the same screen as the baja.
  const [exclusion, setExclusion] = useState(false);
  const [word, setWord] = useState('');
  const [reason, setReason] = useState('');
  const titleId = useId();
  // Floating and not in the form (ADR 0021). Two things this buys and one it costs: the failure
  // outlives the dialog — an agent who closes it still has the sentence on screen — and the form
  // below is left exactly as it was, because the next thing they do is press the button again. The
  // cost is that MUI hides every body-level sibling of an open modal from assistive tech, so a
  // notice raised BEFORE this dialog opened is `aria-hidden` behind it; this one is raised while it
  // is open, which puts its node outside that sweep.
  useNoticeOnError(isError, NOTICE_ID, WRITE_ERROR);

  // The friction that guards the account is typing the word out IN FULL UPPERCASE — the casing is
  // part of the deliberate act. Only a space the clipboard brought along is forgiven.
  const canConfirm =
    word.trim() === 'ELIMINAR' && (!courier || normalizeReason(reason) !== '');
  const copy = courier ? COPY.courier : COPY.user;

  // The guard has to live HERE. The façade always honours Escape, and without
  // `disableBackdropClose` a click outside as well, so this handler is the only place either door
  // can be refused — and while the baja is in flight there is nothing left to retreat from: a door
  // that still closed would leave the agent believing they had called off a request they can no
  // longer take back. The ✕ and «Cancelar» are refused by going `disabled`, which this cannot do.
  const handleClose = () => {
    if (isPending) return;
    onCancel();
  };

  // On `onExited`, never on `onClose`: MUI keeps the paper on screen for the whole exit transition,
  // so clearing on close blanks the form in front of the agent on the way out. The component
  // outlives its own modal — MUI unmounts the paper, not the state up here — so without this the
  // next open inherits whatever the last one was abandoned holding.
  const handleExited = () => {
    setWord('');
    setExclusion(false);
    setReason('');
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      onExited={handleExited}
      label={copy.label}
      // This dialog HAS a heading, so the heading is what names it and `label` stays the fallback.
      labelledBy={titleId}
      title={
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          {/* Decorative: the caution is spelled out in the heading beside it and in the body, so
              nothing is carried by the glyph alone. */}
          <WarningIcon color="warning" />
          <Text component="h2" variant="h6" id={titleId}>
            {copy.heading}
          </Text>
          {/* Pushes the ✕ to the far edge, so the heading keeps the left margin the body is set to
              rather than being centred by a control that arrived after it. */}
          <Box sx={{ flexGrow: 1 }} />
          <IconButton aria-label="Cerrar" onClick={onCancel} disabled={isPending}>
            <CloseIcon />
          </IconButton>
        </Stack>
      }
      actions={
        <>
          {/* TODO: i18n — copy en ES hasta Fase 1. */}
          <Button color="neutral" sentenceCase onClick={onCancel} disabled={isPending}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            sentenceCase
            loading={isPending}
            disabled={!canConfirm}
            onClick={() => {
              if (courier) onConfirm(exclusion, normalizeReason(reason));
              else onConfirm(exclusion);
            }}
          >
            Eliminar cuenta
          </Button>
        </>
      }
    >
      {/* Typography carries no margin of its own, so the body's rhythm is the stack's or nothing —
          without it the warning, the two section titles and their controls sit flush together. */}
      <Stack spacing={2}>
        {/* TODO: i18n — copy en ES hasta Fase 1. */}
        <Text>
          Esta acción es <strong>irreversible</strong>. Se anonimizará la cuenta y todos sus datos
          asociados.
        </Text>
        {/* Offered only to an account with Amazon delivery history — there is no list to exclude
            from otherwise. Hidden entirely (not disabled), so the modal reads as one gate for these
            accounts and two for the rest. */}
        {showAmazonExclusion && (
          <>
            <Text bold>Exclusión de carriers</Text>
            <Checkbox
              checked={exclusion}
              onChange={setExclusion}
              highlight
              label="El usuario no quiere recibir más paquetes de Amazon"
            />
          </>
        )}
        {/* No `autoFocus`, unlike the block's: the baja sets no focus of its own. */}
        {courier && <ReasonField value={reason} onChange={setReason} />}
        <Text bold>Confirmación</Text>
        {/* Body text, deliberately NOT the field's `label`: this is the instruction the whole gate
            rests on, and a form caption is set a step down in size and tone from the prose above. */}
        <Text>
          Confirma la acción escribiendo <strong>ELIMINAR</strong>.
        </Text>
        <TextField
          value={word}
          onChange={setWord}
          fullWidth
          placeholder="ELIMINAR"
          aria-label="Confirmación de la baja"
          helperText="Escribe ELIMINAR para habilitar el botón"
        />
      </Stack>
    </Dialog>
  );
}
